import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Instagram, Twitter, Youtube, Linkedin } from "lucide-react";
import api from "@/lib/api";

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, twitter: Twitter, youtube: Youtube, linkedin: Linkedin };

const DEFAULT_LEGAL_LINKS = [
  { label: "Privacy Policy", path: "/privacy-policy" },
  { label: "Terms & Conditions", path: "/terms-and-conditions" },
  { label: "Cancellation & Refund Policy", path: "/cancellation-refund-policy" },
  { label: "Shipping & Delivery Policy", path: "/shipping-delivery-policy" },
  { label: "Blog / Press", path: "/blog" },
];

export default function Footer() {
  const [settings, setSettings] = useState(null);
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/site-settings").then((res) => setSettings(res.data)).catch(() => {});
    api.get("/services").then((res) => setServices(res.data.slice(0, 5))).catch(() => {});
  }, []);

  const legalLinks = settings?.footer_legal_links || DEFAULT_LEGAL_LINKS;

  return (
    <footer data-testid="site-footer" className="bg-[#1A1A1A] text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">
        <div>
          {settings?.logo_url ? (
            <img src={settings.logo_url} alt="Gulfudaan" data-testid="footer-logo-image" className="h-10 w-auto object-contain brightness-0 invert" />
          ) : (
            <span className="font-heading text-2xl font-bold">Gulf<span className="text-[var(--brand-primary)]">udaan</span></span>
          )}
          <p className="text-sm text-gray-400 mt-4 leading-relaxed" data-testid="footer-tagline">
            {settings?.footer_tagline || "Your trusted partner for GAMCA appointments, visa services, and Gulf immigration solutions across India."}
          </p>
          <div className="flex items-center gap-3 mt-5">
            {settings?.social_links && Object.entries(settings.social_links).map(([key, url]) => {
              const Icon = SOCIAL_ICONS[key];
              if (!Icon || !url) return null;
              return (
                <a key={key} href={url} target="_blank" rel="noopener noreferrer" data-testid={`footer-social-${key}`}
                  className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[var(--brand-primary)] transition-colors">
                  <Icon size={16} />
                </a>
              );
            })}
          </div>
        </div>

        <div>
          <h4 className="font-heading text-lg mb-4 text-[var(--brand-primary)]">Services</h4>
          <ul className="space-y-2 text-sm text-gray-400">
            {services.map((s) => (
              <li key={s.slug}><Link data-testid={`footer-service-${s.slug}`} to={`/services/${s.slug}`} className="hover:text-white transition-colors">{s.name}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-heading text-lg mb-4 text-[var(--brand-primary)]">Partner With Us</h4>
          <ul className="space-y-2 text-sm text-gray-400">
            <li><Link data-testid="footer-become-agent" to="/become-agent" className="hover:text-white transition-colors">Become a B2B Agent</Link></li>
            <li><Link data-testid="footer-become-partner" to="/become-partner" className="hover:text-white transition-colors">Become a Partner</Link></li>
            <li><Link data-testid="footer-refer-earn" to="/refer-and-earn" className="hover:text-white transition-colors">Refer &amp; Earn</Link></li>
            <li><Link data-testid="footer-my-coupons" to="/my-coupons" className="hover:text-white transition-colors">My Coupons</Link></li>
            <li><Link data-testid="footer-track" to="/track" className="hover:text-white transition-colors">Track Application</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-heading text-lg mb-4 text-[var(--brand-primary)]">Legal</h4>
          <ul className="space-y-2 text-sm text-gray-400">
            {legalLinks.map((l) => (
              <li key={l.path}><Link data-testid={`footer-legal-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} to={l.path} className="hover:text-white transition-colors">{l.label}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-heading text-lg mb-4 text-[var(--brand-primary)]">Contact Us</h4>
          <ul className="space-y-3 text-sm text-gray-400">
            <li className="flex items-start gap-2" data-testid="footer-address"><MapPin size={16} className="mt-0.5 shrink-0 text-[var(--brand-primary)]" /> {settings?.address || "Ground Floor, Classic Ashiyana II, Nazarbagh, Lucknow - 226001, Uttar Pradesh, India"}</li>
            <li className="flex items-center gap-2" data-testid="footer-phone"><Phone size={16} className="text-[var(--brand-primary)]" /> {settings?.phone || "+91 73143 44673"}</li>
            <li className="flex items-center gap-2" data-testid="footer-email"><Mail size={16} className="text-[var(--brand-primary)]" /> {settings?.email || "help@gulfudaan.com"}</li>
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 pt-6 border-t border-white/10 text-center text-xs text-gray-500">
        © {new Date().getFullYear()} Gulfudaan. All rights reserved.
      </div>
    </footer>
  );
}
